import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, X, Edit3 } from "lucide-react";
import { MultiSelect } from "@/components/ui/multi-select";
import type { GruppeDTO } from "@/lib/orgaAdmin/orgaAdmin.types";

interface SearchFilterBarProps {
	searchTerm: string;
	onSearchChange: (value: string) => void;
	filterGroupIds: string[];
	onFilterGroupsChange: (groupIds: string[]) => void;
	availableGroups: GruppeDTO[];
	selectedCount: number;
	onBulkEdit: () => void;
	onClearFilters: () => void;
}

/*
 * SearchFilterBar component
 * @param searchTerm - The search term
 * @param onSearchChange - The function to change the search term
 * @param filterGroupIds - The filter group ids
 * @param onFilterGroupsChange - The function to change the filter group ids
 * @param availableGroups - The available groups
 * @param selectedCount - The selected count
 * @param onBulkEdit - The function to bulk edit
 * @param onClearFilters - The function to clear the filters
 */
export const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
	searchTerm,
	onSearchChange,
	filterGroupIds,
	onFilterGroupsChange,
	availableGroups,
	selectedCount,
	onBulkEdit,
	onClearFilters,
}) => {
	const hasActiveFilters =
		filterGroupIds.length > 0 || searchTerm.trim().length > 0;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-row">
				{/* Search Bar */}
				<div className="relative flex flex-row flex-1 gap-1">
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
					<Input
						placeholder="Nach Person suchen (Name oder E-Mail)..."
						value={searchTerm}
						onChange={(e) => onSearchChange(e.target.value)}
						className="pl-10 h-12 text-base border-gray-300 focus:border-blue-500 focus:ring-blue-500"
					/>
					{searchTerm && (
						<button
							type="button"
							onClick={() => onSearchChange("")}
							className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
						>
							<X className="h-4 w-4 text-gray-400" />
						</button>
					)}
					{selectedCount > 0 && (
						<div className="flex flex-row gap-2 items-center">
							<Button
								onClick={onBulkEdit}
								className="flex items-center gap-2 text-sm bg"
							>
								<Edit3 className="h-3 w-3" />
								{selectedCount}
								{" bearbeiten"}
							</Button>
						</div>
					)}
				</div>

				{/* Filters and Actions */}
				<div className="flex items-center justify-between ml-6">
					<div className="flex items-center">
						<div className="min-w-0 flex-1 max-w-xs flex items-center gap-2">
							<Filter className="h-4 w-4 text-gray-500" />
							<MultiSelect
								options={availableGroups.map((g) => ({
									value: g.id,
									label: g.title,
								}))}
								values={filterGroupIds}
								onChange={onFilterGroupsChange}
								placeholder="Gruppen filtern"
							/>
						</div>
						{hasActiveFilters && (
							<Button
								variant="ghost"
								size="sm"
								onClick={onClearFilters}
								className="text-gray-500 hover:text-gray-700"
							>
								Filter zurücksetzen
							</Button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default SearchFilterBar;
